import { keys, merge, pick } from 'lodash-es';
import { FilterCriteriaItem, PageParam, SortParam } from './appDomain';
import { MeasurementUnitEnum } from './measurementDomain';
import { EqptSchedule } from './tfDomain';

export interface Equipment {
  /**
   * The unit the numbers in a write were typed in. Request-only: never stored on the row and
   * never sent back, but it has to live on this type so `buildUpdate/CreateEqptParam` can carry
   * it through. Dropping it stored a metric 210 as 210in.
   */
  enteredUnit?: MeasurementUnitEnum;

  id: number;
  revxId: number;
  active: boolean;
  deleted: boolean;
  isBase: boolean;
  baseRefId: number;
  name: string;
  manufacturer: string;
  printUnits: number;
  avgRunSpeedLarge: number;
  avgRunSpeedSmall: number;
  bwClick: number;
  colorClick: number;
  markupPct: number;
  hourlyIncrement: number;
  makereadyCostUsd: number;
  printUnitCostUsd: number;
  plateUnitCostUsd: number;
  setupUnitCostUsd: number;
  pmsInkSqinCostUsd: number;
  metallicInkSqinCostUsd: number;
  minWasteSheets: number;
  fixedWasteSheetsActive: boolean;
  fixedWasteSheets: number;
  washupUnitCostUsd: number;
  inkColorSqinCostUsd: number;
  coatingSqinCostUsd: number;
  costPerFoldUsd: number;
  costPerStapleUsd: number;
  perfectorConfig: string;
  caliperMinIn: number;
  caliperMaxIn: number;
  sheetMinWidthIn: number;
  sheetMaxWidthIn: number;
  sheetMinLengthIn: number;
  sheetMaxLengthIn: number;
  imageMaxLengthIn: number;
  imageMaxWidthIn: number;
  coatingUnit: number;
  gripperIn: number;
  maxLiftLb: number;
  createAt: string;
  updateAt: string;
  orgId: number;
  type: EqptType;
  version: number;
  defaultTurnaroundTimes: EqptSchedule[];
  exceptionTurnaroundTimes: EqptSchedule[];
  wasteFactorPct: number;
  cutOffIn: number;
  cylinderGapIn: number;
}

export interface EqptType {
  id: number;
  code: string;
  name: string;
  productionType: string;
  costingMethod: string;
  createAt: string;
  updateAt: string;
}

export interface ListEqptParam {
  page: PageParam;
  query?: EqptFilterObj;
  sort?: SortParam;
}

export type PartialEqpt = Partial<Equipment>;

export type UpdateEqptParam = Omit<
  PartialEqpt,
  | 'revxId'
  | 'isBase'
  | 'baseRefId'
  | 'createAt'
  | 'updateAt'
  | 'orgId'
  | 'type'
  | 'deleted'
>;

export type CreateEqptParam =
  | { typeCode: string }
  | Omit<
      PartialEqpt,
      'createAt' | 'updateAt' | 'orgId' | 'type' | 'deleted' | 'version'
    >;

export const EMPTY_EQPT: PartialEqpt = {
  revxId: 0,
  active: true,
  isBase: true,
  baseRefId: 0,
  name: '',
  manufacturer: '',
  printUnits: 0,
  avgRunSpeedLarge: 0,
  avgRunSpeedSmall: 0,
  bwClick: 0,
  colorClick: 0,
  markupPct: undefined,
  hourlyIncrement: 0,
  pmsInkSqinCostUsd: 0,
  metallicInkSqinCostUsd: 0,
  minWasteSheets: 0,
  fixedWasteSheetsActive: false,
  fixedWasteSheets: 0,
  makereadyCostUsd: 0,
  printUnitCostUsd: 0,
  plateUnitCostUsd: 0,
  setupUnitCostUsd: 0,
  washupUnitCostUsd: 0,
  inkColorSqinCostUsd: 0,
  coatingSqinCostUsd: 0,
  costPerFoldUsd: 0,
  costPerStapleUsd: 0,
  perfectorConfig: '',
  caliperMinIn: 0,
  caliperMaxIn: 0,
  sheetMinWidthIn: 0,
  sheetMaxWidthIn: 0,
  sheetMinLengthIn: 0,
  sheetMaxLengthIn: 0,
  imageMaxLengthIn: 0,
  imageMaxWidthIn: 0,
  coatingUnit: 0,
  gripperIn: 0,
  maxLiftLb: 0,
  wasteFactorPct: 0,
  cutOffIn: 0,
  cylinderGapIn: 0,
};

/** `enteredUnit` is picked explicitly - see the note on `buildUpdateMatParam`. */
export function buildUpdateEqptParam(eqpt: PartialEqpt): UpdateEqptParam {
  return pick(eqpt, keys(EMPTY_EQPT), 'enteredUnit', 'id', 'version');
}

export function buildCreateEqptParam(eqpt: PartialEqpt): CreateEqptParam {
  return merge(
    { typeCode: eqpt.type?.code },
    pick(eqpt, keys(EMPTY_EQPT), 'enteredUnit')
  );
}

export interface EqptFilterObj {
  id?: FilterCriteriaItem;
  revxId?: FilterCriteriaItem;
  name?: FilterCriteriaItem;
  active?: boolean;
  avgRunSpeedLarge?: FilterCriteriaItem;
  avgRunSpeedSmall?: FilterCriteriaItem;
  coatingUnit?: FilterCriteriaItem;
  hourlyIncrement?: FilterCriteriaItem;
  manufacturer?: FilterCriteriaItem;
  markupPct?: FilterCriteriaItem;
  perfectorConfig?: FilterCriteriaItem;
  printUnits?: FilterCriteriaItem;
  typeId?: FilterCriteriaItem;
}

export interface EquipmentTypesItemEquipment {
  active: boolean;
  equipmentId: number;
  equipmentName: string;
  equipmentManufacturer: string;
  equipmentTypeCode: string;
}

export interface EquipmentTypesItem {
  equipmentTypeCode: string;
  equipmentTypeName: string;
  equipments: EquipmentTypesItemEquipment[];
}

export interface EquipmentsAvailablePress {
  currentConfig: {
    equipmentCodes: string[];
    exceptEquipmentIds: number[];
  };
  equipmentTypes: EquipmentTypesItem[];
  orgEquipments: EquipmentTypesItemEquipment[];
}
